import React, { useEffect, useRef, useState } from 'react';
import { apiFetch } from '../lib/api';

const BLANK = { name: '', title: '', phone: '', email: '', company: '', website: '' };

export default function Signature() {
  const [form, setForm] = useState(BLANK);
  const [html, setHtml] = useState('');
  const [stats, setStats] = useState([]);
  const [msg, setMsg] = useState('');
  const [copied, setCopied] = useState(false);
  const [copiedSig, setCopiedSig] = useState(false);
  const previewRef = useRef(null);

  async function load() {
    const res = await apiFetch('/api/signature');
    if (res.ok) {
      const d = await res.json();
      if (d.signature) setForm({ ...BLANK, ...d.signature, title: d.signature.title || '', phone: d.signature.phone || '', email: d.signature.email || '', company: d.signature.company || '', website: d.signature.website || '' });
    }
    refreshDerived();
  }
  async function refreshDerived() {
    const [h, s] = await Promise.all([apiFetch('/api/signature/html'), apiFetch('/api/signature/stats')]);
    if (h.ok) { const d = await h.json(); setHtml(d.html || ''); }
    if (s.ok) { const d = await s.json(); setStats(d.stats || []); }
  }
  useEffect(() => { load(); }, []);

  async function save() {
    setMsg('');
    const res = await apiFetch('/api/signature', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    });
    if (res.ok) { setMsg('Saved.'); refreshDerived(); }
    else { const d = await res.json().catch(() => ({})); setMsg(d.error || 'Could not save.'); }
  }

  function copyHtml() {
    if (!html) return;
    navigator.clipboard.writeText(html).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  }

  async function copyRendered() {
    const node = previewRef.current;
    if (!node) return;
    const markup = node.innerHTML;
    try {
      if (navigator.clipboard && window.ClipboardItem) {
        const item = new window.ClipboardItem({
          'text/html': new Blob([markup], { type: 'text/html' }),
          'text/plain': new Blob([node.innerText || ''], { type: 'text/plain' }),
        });
        await navigator.clipboard.write([item]);
      } else {
        // Fallback: select the node and execCommand('copy').
        const range = document.createRange();
        range.selectNodeContents(node);
        const sel = window.getSelection();
        sel.removeAllRanges(); sel.addRange(range);
        document.execCommand('copy');
        sel.removeAllRanges();
      }
      setCopiedSig(true); setTimeout(() => setCopiedSig(false), 2000);
    } catch (e) { /* clipboard blocked; user can select manually */ }
  }

  function fmt(s) { if (!s) return 'never'; try { return new Date(s).toLocaleDateString(); } catch { return '—'; } }
  const field = (k, label, type = 'text') => (
    <input className="hq-input" type={type} placeholder={label} value={form[k]}
      onChange={(e) => setForm({ ...form, [k]: e.target.value })} style={{ minWidth: 200 }} />
  );

  return (
    <div className="hq-page-content">
      <div className="hq-page-head">
        <h1>Email Signature</h1>
        <p>Build your signature; links are click-tracked.</p>
      </div>

      <div className="hq-panel" style={{ marginBottom: 20 }}>
        <div className="hq-panel-head">
          <h2>Details</h2>
          <button className="hq-btn" onClick={save}>Save</button>
        </div>
        <div style={{ padding: 20 }}>
          <div className="hq-form">
            {field('name', 'Full name')}
            {field('title', 'Job title')}
            {field('company', 'Company')}
            {field('phone', 'Phone')}
            {field('email', 'Email', 'email')}
            {field('website', 'Website')}
          </div>
          {msg && <p className="hq-msg">{msg}</p>}
        </div>
      </div>

      <div className="hq-panel" style={{ marginBottom: 20 }}>
        <div className="hq-panel-head">
          <h2>Preview &amp; HTML</h2>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="hq-btn" onClick={copyRendered} disabled={!html}>{copiedSig ? 'Copied!' : 'Copy signature'}</button>
            <button className="hq-btn" onClick={copyHtml} disabled={!html}>{copied ? 'Copied!' : 'Copy HTML'}</button>
          </div>
        </div>
        <div style={{ padding: 20 }}>
          {html ? (
            <>
              <div ref={previewRef} style={{ background: '#fff', borderRadius: 10, padding: 16, marginBottom: 14 }}
                dangerouslySetInnerHTML={{ __html: html }} />
              <textarea readOnly value={html} className="hq-input"
                style={{ width: '100%', height: 120, padding: 12, fontFamily: 'monospace', fontSize: 12, resize: 'vertical' }} />
            </>
          ) : (
            <p className="hq-empty" style={{ padding: 0 }}>Save your details to generate the signature.</p>
          )}
        </div>
      </div>

      <div className="hq-panel hq-install-help" style={{ marginBottom: 20 }}>
        <div className="hq-panel-head"><h2>Install your signature</h2></div>
        <div style={{ padding: 20, fontSize: 13, lineHeight: 1.6, color: 'var(--hq-muted)' }}>
          <p style={{ margin: '0 0 14px', color: 'var(--hq-txt)' }}><strong>macOS Mail</strong></p>
          <ol style={{ margin: '0 0 18px', paddingLeft: 18 }}>
            <li>In the preview above, select the whole signature and copy it (Cmd+C). Copy the rendered signature, not the HTML code.</li>
            <li>Open Mail &rarr; Settings &rarr; Signatures.</li>
            <li>Pick the email account on the left, then click <strong>+</strong> to add a signature.</li>
            <li>Uncheck <strong>Always match my default message font</strong>.</li>
            <li>Click into the editing area and paste (Cmd+V).</li>
            <li>Close Settings — Mail saves automatically. Send yourself a test email.</li>
          </ol>
          <p style={{ margin: '0 0 14px', color: 'var(--hq-txt)' }}><strong>iPhone / iPad (iOS Mail)</strong></p>
          <ol style={{ margin: 0, paddingLeft: 18 }}>
            <li>Email the signature to yourself (or copy it on your Mac and AirDrop / send a test).</li>
            <li>Open that email on your iPhone. Press and hold a non-link part of the signature, then tap <strong>Select All</strong> &rarr; <strong>Copy</strong>.</li>
            <li>Go to Settings &rarr; Mail &rarr; Signature. Delete the default text and paste.</li>
            <li>The paste may look unstyled at first. <strong>Shake the phone</strong> and tap <strong>Undo Change Attributes</strong> to restore the formatting.</li>
            <li>Tap back to save, then send a test email to confirm.</li>
          </ol>
          <p style={{ margin: '16px 0 0' }}>The logo loads from the web, so recipients see it only when their mail client allows remote images.</p>
        </div>
      </div>

      <div className="hq-panel">
        <div className="hq-panel-head"><h2>Link clicks</h2></div>
        <table className="hq-table">
          <thead><tr><th>Link</th><th>Clicks</th><th>Last click</th></tr></thead>
          <tbody>
            {stats.map((s) => (
              <tr key={s.kind}>
                <td style={{ textTransform: 'capitalize' }}>{s.kind}</td>
                <td>{s.clicks}</td>
                <td>{fmt(s.last_click)}</td>
              </tr>
            ))}
            {stats.length === 0 && (
              <tr><td className="hq-empty" colSpan={3}>No tracked links yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
